import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Plus } from "lucide-react";
import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";

// Your publishable key
const stripePromise = loadStripe(
  "pk_test_51Q0WtHGWZaE8Effy85o3KJFWIlhKwBz853zXlX1nHVzOJKJwIEHXjpDdoeYZjXLcnZ8lSzikibgQZFsgFMjhBOol00fhvgsfqC"
);

interface propTypes {
  handleRefresh: () => void;
}

const AddPaymentMethodModal = ({ handleRefresh }: propTypes) => {
  const [show, setShow] = useState<boolean>(false);
  const toggle = () => setShow((prev) => !prev);

  return (
    <>
      <button
        className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-lg hover:scale-105 w-full sm:w-auto"
        onClick={toggle}
      >
        <Plus size={20} />
        <span>Add Payment Method</span>
      </button>

      {show && (
        <Elements stripe={stripePromise}>
          <StripeCardForm toggle={toggle} handleRefresh={handleRefresh} />
        </Elements>
      )}
    </>
  );
};

const StripeCardForm = ({
  toggle,
  handleRefresh,
}: propTypes & { toggle: () => void }) => {
  const stripe = useStripe();
  const { user, Update } = useUserInfo();
  const AppService = useAppServices();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name on card is required"),
    }),
    onSubmit: async (values) => {
      try {
        setErrorMsg(null);
        setLoading(true);

        if (!stripe || !elements) {
          setErrorMsg("Stripe has not loaded yet.");
          return;
        }

        // Get the card element instance
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          setErrorMsg("Card element not found.");
          return;
        }

        // Create a payment method via Stripe
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: values.name,
          },
        });

        if (error) {
          throw new Error(error.message);
        }
        const payload = {
          cardHolderName: values?.name,
          paymentMethodType: "company",
          provider: "stripe",
          companyId: user?.companyId,
          userId: user?._id,
          paymentMethodId: paymentMethod.id,
          brand: paymentMethod.card?.brand || "unknown",
          last4: paymentMethod.card?.last4 || null,
          expMonth: paymentMethod.card?.exp_month || null,
          expYear: paymentMethod.card?.exp_year || null,
        };
        const { response } = await AppService.payment_method.create({
          payload,
          toaster: true,
        });
        if (response) {
          Update(response.user);
          handleRefresh();
          toggle();
          formik.resetForm();
        }
      } catch (err: any) {
        setErrorMsg(err.message || "An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Modal
      show
      onHide={toggle}
      id="AddPaymentMethodModal"
      modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-screen md:w-[40rem] bg-white shadow rounded-md dark:bg-zink-600"
    >
      <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
        <Modal.Title className="text-16 font-semibold">Add Card</Modal.Title>
      </Modal.Header>

      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <form onSubmit={formik.handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              Name on Card
            </label>
            <input
              type="text"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              placeholder="John Doe"
            />
            {formik.touched.name && formik.errors.name && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                {formik.errors.name}
              </p>
            )}
          </div>

          {/* Card Element */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              Card Details
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-white dark:bg-gray-700">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#32325d",
                      "::placeholder": { color: "#a0aec0" },
                      backgroundColor: "transparent",
                    },
                    invalid: { color: "#e53e3e" },
                  },
                }}
              />
            </div>
          </div>

          {/* Error */}
          {errorMsg && (
            <div className="text-red-500 dark:text-red-400 text-sm">
              {errorMsg}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              className="text-red-500 dark:text-red-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 btn hover:text-red-600 dark:hover:text-red-300 hover:bg-gray-50 dark:hover:bg-gray-600"
              onClick={() => {
                formik.resetForm();
                toggle();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !stripe}
              className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 dark:bg-custom-500 dark:border-custom-500 dark:hover:bg-custom-600 disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add Card"}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddPaymentMethodModal;
